import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSidebarData } from '../../hooks/sidebarData';
import { destroySession } from '../../helpers/Session';
import NDOTDepartmentLoader from '../loader/NDOTDepartmentLoader.js';
import axios from '../../api/axios';
import Swal from 'sweetalert2';
import toast from '../sweetalert/Sweetalert';
import DataTable from 'react-data-table-component';

const AllNDOTRequests = ({ departmentslug }) => {
    const APPROVED_STATUS = 1;
    const DISAPPROVED_STATUS = 2;

    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { setSidebarData } = useSidebarData();

    const timeoutRef = useRef(null);

    const accesstoken = sessionStorage.getItem('accesstoken');
    const uname = sessionStorage.getItem('uname');
    const email = sessionStorage.getItem('email');

    const [currentPage, setCurrentPage] = useState(1);
    const [rowPerPage, setRowPerPage] = useState(10); 

    const departmentNDOTDataKey = ['departmentNDOTRequests', { departmentslug, currentPage, rowPerPage }];

    const tableColumns = [
        { name: 'Name', selector: row => row.name },
        { name: 'Request Type', selector: row => row.requesttype },
        { name: 'Start Date', selector: row => row.startdate },
        { name: 'End Date', selector: row => row.enddate },
        { name: 'Status', selector: row => row.status },
        { name: 'Action', selector: row => row.action }
    ];

    const handleViewNDOTRequest = (ndotid, uname) => {
        setSidebarData('state', true);
        setSidebarData('key', 'ndot-request');
        setSidebarData('label', `NDOT Request Details`);
        setSidebarData('data', {
            uname,
            ndotrequestid: ndotid
        });
    };

    const handleViewNDOTRequestStatus = (uname, name, ndotid, sdate, edate) => {
        setSidebarData('state', true);
        setSidebarData('key', 'ndot-request-status');
        setSidebarData('label', `[${name}] NDOT Request Status`);
        setSidebarData('data', {
            uname,
            ndotrequestid: ndotid,
            sdate,
            edate
        });
    }

    const ndotRequestApprovalDisapprovalMutation = useMutation({
        mutationFn: async (ndotRequestData) => {
            const res = await axios.patch('ndot', ndotRequestData, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data.response;
        },
        onSuccess: (data) => {
            toast.fire({ icon: 'success', title: data });
            queryClient.invalidateQueries(departmentNDOTDataKey);
        },
        onError: (error) => {
            const errorMessage = error.response?.data.response || 'Something went wrong while updating the status of the NDOT request!';
            toast.fire({ icon: 'error', title: errorMessage });
            if (error.response?.data.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error') }, 1000);
            }
        }
    });

    const handleNDOTRequestApproval = (name, statuslabel, status, uname, ndotrequestid, department, approver, approvalid) => {
        Swal.fire({
            text: `Are you sure you want to ${statuslabel} NDOT request of ${name}?`,
            icon: 'warning',
            allowOutsideClick: false,
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            showLoaderOnConfirm: true,
            preConfirm: function(result) {
                return new Promise(function(resolve) {
                    if (result) {
                        ndotRequestApprovalDisapprovalMutation.mutate({
                            status: status,
                            uname: uname,
                            ndotrequestid: ndotrequestid,
                            department: department,
                            updator: approver,
                            approvalID: approvalid
                        });
                        resolve();
                    }  
                });
            }
        });
    }

    const handleNDOTRequestDisapproval = (name, statuslabel, status, uname, ndotrequestid, department, approver, approvalid) => {
        Swal.fire({
            text: `Are you sure you want to ${statuslabel} NDOT request of ${name}?`,
            icon: 'warning',
            allowOutsideClick: false,
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            showLoaderOnConfirm: true,
            preConfirm: function(result) {
                return new Promise(function(resolve) {
                    if (result) {
                        Swal.fire({
                            title: `Disapproval Reason`,
                            input: "text",
                            inputAttributes: {
                                autocomplete: "off"
                            },
                            allowOutsideClick: false,
                            showCancelButton: true,
                            showLoaderOnConfirm: true,
                            confirmButtonColor: '#3085d6',
                            cancelButtonColor: '#d33',
                            confirmButtonText: 'Yes',
                            cancelButtonText: 'No',
                            preConfirm: async (reason) => {
                                ndotRequestApprovalDisapprovalMutation.mutate({
                                    status: status,
                                    uname: uname,
                                    ndotrequestid: ndotrequestid,
                                    department: department,
                                    updator: approver,
                                    approvalID: approvalid,
                                    statusreason: reason
                                });
                            }
                        });
                    }  
                });
            }
        });
    }

    const { data: ndot, isLoading: isLoadingNDOT, error: errorNDOT } = useQuery({
        queryKey: departmentNDOTDataKey,
        queryFn: async () => {
            const res = await axios.get(`ndot/requests?department=${departmentslug}&page=${currentPage}&rows_per_page=${rowPerPage}&email=${email}`, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            const data = res.data.response?.requests?.map(item => ({
                name: item.name,
                requesttype: item.requesttype,
                startdate: item.startdate,
                enddate: item.enddate,
                status: (
                        item.status === 1 
                            ? <span className="label label-primary" onClick={() => handleViewNDOTRequestStatus(item.uname, item.name, item.id, item.start_date, item.end_date)}>Pending</span> 
                            : (item.status === 2 
                                ? <span className="label label-success" onClick={() => handleViewNDOTRequestStatus(item.uname, item.name, item.id, item.start_date, item.end_date)}>Approved</span> 
                                : (item.status === 3 
                                    ? <span className="label label-danger" onClick={() => handleViewNDOTRequestStatus(item.uname, item.name, item.id, item.start_date, item.end_date)}>Disapproved</span> 
                                    : (item.status === 4 
                                        ? <span className="label label-warning" onClick={() => handleViewNDOTRequestStatus(item.uname, item.name, item.id, item.start_date, item.end_date)}>Cancelled</span> 
                                        : <span className="label label-primary" onClick={() => handleViewNDOTRequestStatus(item.uname, item.name, item.id, item.start_date, item.end_date)}>Pending</span>)))
                ),
                action: (
                    <div className="buttons">
                        <button type="button" className="btn-view" title="VIEW" onClick={() => handleViewNDOTRequest(item.id, item.uname)}>
                            <i className="fa fa-eye"></i>
                        </button>
                        {item.approvalid > 0 && (
                            <>
                                <button type="button" className="btn-approve" title="APPROVE" onClick={() => handleNDOTRequestApproval(item.name,'approve', APPROVED_STATUS, item.uname, item.id, departmentslug, uname, item.approvalid)}>
                                    <i className="fa fa-check"></i>
                                </button>
                                <button type="button" className="btn-disapprove" title="DISAPPROVE" onClick={() => handleNDOTRequestDisapproval(item.name,'disapprove', DISAPPROVED_STATUS, item.uname, item.id, departmentslug, uname, item.approvalid)}>
                                    <i className="fa fa-times"></i>
                                </button>
                            </>
                        )}
                    </div>
                )
            }));

            return { data, totalRows: res.data.response?.totalRows }
        }
    });
        
    useEffect(() => {
        if (errorNDOT) {
            if (errorNDOT.response?.data.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error') }, 1000);
            }
        }
    }, [errorNDOT]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    }

    const handleRowsPerPageChange = (newPerPage) => {
        setRowPerPage(newPerPage);
        setCurrentPage(1);
    };

    return (
        <div className='panels'>
            <div className='left-panel'>
                <DataTable 
                    columns={tableColumns}
                    data={ndot?.data || []}
                    progressPending={isLoadingNDOT}
                    progressComponent={<NDOTDepartmentLoader />}
                    pagination
                    paginationServer
                    paginationTotalRows={ndot?.totalRows || 0}
                    onChangeRowsPerPage={handleRowsPerPageChange}
                    onChangePage={handlePageChange}
                    dense
                    highlightOnHover
                    responsive
                />
            </div>
        </div>
    );
}

export default AllNDOTRequests;